/**
 * Auth gate for guest “Sign in to save views” (ADR-MV-11 / EV-G1-PARK).
 *
 * Elevates via {@link parkMapResumeReturnTarget}: durable map slot beats leftover
 * payment / orders / platform; fail-closed null refuses navigate.
 *
 * Sequence:
 * 1. Write wizard draft to `customer:directory:mapViewWizardDraft:v1` (ADR-MV-10)
 * 2. Elevate-park return target (null → caller must not navigate)
 * 3. Caller navigates to `resolveCustomerSignInPath(tenantCode)`
 * 4. After auth: consume parks → same map URL with `view=map`; wizard draft restore
 */
import { resolveCustomerSignInPath } from '../auth/customerAuthPaths.js';
import { parkMapResumeReturnTarget } from '../../shared/session/returnTarget.js';
import {
  resolveMapViewWizardDraftForSignInPark,
  writeMapViewWizardDraft,
  type MapViewWizardDraftV1,
} from './mapViewWizardDraft.js';

/** i18n key for guest CTA copy. */
export const SIGN_IN_TO_SAVE_VIEWS_I18N_KEY =
  'directory.mapView.signInToSaveViews' as const;

/** data-testid for guest CTA in Views menu. */
export const SIGN_IN_TO_SAVE_VIEWS_TEST_ID =
  'customer-shops-directory-sign-in-to-save-views' as const;

/**
 * Elevate-park the map URL so post-auth returns here (not leftover payment /
 * orders / hub). Returns the stored path (`view=map` ensured) or null.
 */
export function parkMapViewSignInReturnTarget(
  path: string,
  tenantCode: string,
): string | null {
  return parkMapResumeReturnTarget(path, {
    tenantCode,
    source: 'shop',
  });
}

export type BeginSignInToSaveMapViewsOptions = {
  /** Full path+query to resume after auth (usually current location). */
  readonly path: string;
  readonly tenantCode: string;
  /**
   * Fallback draft when session has none (ADR-MV-10 PRIMARY).
   * G2: thin guest park must not overwrite a richer session draft.
   */
  readonly draft: MapViewWizardDraftV1;
};

export type BeginSignInToSaveMapViewsResult = {
  /** Sign-in href — navigate here only when {@link parked} is non-null. */
  readonly signInHref: string;
  /** Whether draft write was attempted (session best-effort). */
  readonly draftWritten: true;
  /** Stored map resume path; null → stay on map (do not navigate to sign-in). */
  readonly parked: string | null;
};

/**
 * Guest CTA entry: persist draft → elevate-park → return sign-in href.
 * Does not navigate — Views menu must skip navigate when `parked === null`.
 *
 * G2: `options.draft` is a fallback — thin guest park never overwrites a richer
 * `customer:directory:mapViewWizardDraft:v1` already in session (ADR-MV-10 SSOT).
 */
export function beginSignInToSaveMapViews(
  options: BeginSignInToSaveMapViewsOptions,
): BeginSignInToSaveMapViewsResult {
  const draft = resolveMapViewWizardDraftForSignInPark(options.draft);
  writeMapViewWizardDraft(draft);
  const parked = parkMapViewSignInReturnTarget(options.path, options.tenantCode);
  return {
    signInHref: resolveCustomerSignInPath(options.tenantCode),
    draftWritten: true,
    parked,
  };
}

/** Minimal draft when guest taps Views CTA without an open wizard. */
export function buildGuestSignInMapViewDraft(
  nowMs: number = Date.now(),
): MapViewWizardDraftV1 {
  return {
    kind: 'around_me',
    step: 'configure',
    createdAtMs: nowMs,
  };
}
